import type { SupProfileKey } from "./sport-profiles";

export type SportKey =
  | "ski" | "off-piste" | "touring" | "mtb"
  | "surf" | "wind" | "foil" | "downwind-foil" | "wing-foil"
  | "bodyboarding" | "diving" | "swimming" | "sup" | SupProfileKey | "sailing";

export type MountainEntry = {
  slug: string;
  name: string;
  country: string;
  countryCode: string;
  region: string;
  pisteKm: number | null;
  verticalDropM: number | null;
  topAltitudeM: number | null;
  baseElevationM: number | null;
  totalLifts: number | null;
  seasonWindow: string | null;
  avgScore: number | null;
  reliabilityLabel: string | null;
  liftPassDailyEur: number | null;
  apresScale: "Low" | "Mid" | "High";
  glacier: boolean;
  snowPark: boolean;
  bikePark: boolean;
  upliftAvailable: boolean;
  beginnerFriendly: boolean;
  mtbDifficulty: string | null;
  terrainType: string | null;
  treelineTerrain: string | null;
};

export type CoastalEntry = {
  slug: string;
  name: string;
  country: string;
  countryCode: string;
  region: string;
  avgScore: number | null;
  reliabilityLabel: string | null;
  spotType: string | null;
  difficultyLevel: string | null;
  tidePreference: string | null;
  shelterLevel: string | null;
  facingDirection: string | null;
  nightlifeScale: string | null;
  crowdFactor: string | null;
  bottomType: string | null;
  marineHighlights: string | null;
  waterType: string | null;
  avgWaterTempC: number | null;
  typicalWaveDirection: string | null;
  prevailingWindDir: string | null;
  typicalVisibility: string | null;
  maxWaveSize: string | null;
  bestWindDirection: string | null;
  lifeguarded: string | null;
  bestMonths: string;
};

export type ColumnConfig = {
  key: string;
  label: string;
  sortable: boolean;
  defaultVisible: boolean;
  align?: "left" | "right" | "center";
  format?: "score" | "boolean" | "temp" | "number" | "badge" | "text";
};

export type SportConfig = {
  key: SportKey;
  label: string;
  emoji: string;
  category: "mountain" | "coastal";
  scoreColumn: string;
  columns: ColumnConfig[];
  isBeta?: true;
};

export const DEFAULT_DIRECTORY_SPORT_CONFIG: SportConfig = {
  key: "ski",
  label: "Ski",
  emoji: "⛷️",
  category: "mountain",
  scoreColumn: "piste",
  columns: [
    { key: "name", label: "Name", sortable: true, defaultVisible: true, align: "left" },
    { key: "region", label: "Region", sortable: true, defaultVisible: true, align: "left" },
    { key: "country", label: "Country", sortable: true, defaultVisible: true, align: "left" },
    { key: "pisteKm", label: "Piste km", sortable: true, defaultVisible: true, align: "right", format: "number" },
    { key: "verticalDropM", label: "Vertical Drop", sortable: true, defaultVisible: true, align: "right", format: "number" },
    { key: "topAltitudeM", label: "Top Altitude", sortable: true, defaultVisible: true, align: "right", format: "number" },
    { key: "baseElevationM", label: "Base Elevation", sortable: true, defaultVisible: true, align: "right", format: "number" },
    { key: "totalLifts", label: "Lifts", sortable: true, defaultVisible: true, align: "right", format: "number" },
    { key: "seasonWindow", label: "Season", sortable: true, defaultVisible: true, align: "right" },
    { key: "avgScore", label: "Score", sortable: true, defaultVisible: true, align: "right", format: "score" },
    { key: "reliabilityLabel", label: "Reliability", sortable: true, defaultVisible: true, align: "right" },
    { key: "liftPassDailyEur", label: "Lift Pass/day", sortable: true, defaultVisible: true, align: "right", format: "number" },
    { key: "glacier", label: "Glacier", sortable: true, defaultVisible: true, align: "center", format: "boolean" },
    { key: "snowPark", label: "Snow Park", sortable: true, defaultVisible: true, align: "center", format: "boolean" },
    { key: "beginnerFriendly", label: "Beginner Friendly", sortable: true, defaultVisible: true, align: "center", format: "boolean" },
    { key: "apresScale", label: "Après", sortable: true, defaultVisible: true, align: "center", format: "badge" },
  ],
};

export function getSportConfig(
  key: string,
  allowedConfigs: readonly SportConfig[],
): SportConfig {
  const fallback = allowedConfigs[0] ?? DEFAULT_DIRECTORY_SPORT_CONFIG;
  return allowedConfigs.find((sport) => sport.key === key) ?? fallback;
}
